import dayjs from 'dayjs';
import type { ModelCancellationPolicy, ModelOrderResponse } from '@layup/api-sdk';
import { updateOrder } from '../services/orders';
import { displayCurrency } from './format.utils';

const createCancellationPolicy = (
  type: string,
  percentage: number = 0,
  flatFee: number = 0,
): ModelCancellationPolicy => {
  switch (type) {
    case 'Percentage':
      return {
        name: 'Percentage',
        definition: {
          percentage: parseFloat(percentage.toString()),
          flatFee: 0,
          deposit: false,
        },
      };
    case 'Flat Fee':
      return {
        name: 'FlatFee',
        definition: {
          percentage: 0,
          flatFee,
          deposit: false,
        },
      };
    case 'Deposit':
      return {
        name: 'Deposit',
        definition: {
          percentage: 0,
          flatFee: 0,
          deposit: true,
        },
      };
    case 'No Fee':
      return {
        name: 'FlatFee',
        definition: {
          percentage: 0,
          flatFee: 0,
          deposit: false,
        },
      };
    default:
      throw new Error(`Unknown cancellation type: ${type}`);
  }
};

/**
 * Sends a notification for an order.
 * @param order The order to send the notification for.
 * @param cancellationType The type of cancellation.
 * @param message The message to include in the notification.
 * @param user The user details on the cancellation.
 * @param percentage The percentage for the cancellation fee, if applicable. 10 would be 10%.
 * @param fee The flat fee for the cancellation, if applicable in cents.
 */
export const sendOrderNotification = async (
  order: ModelOrderResponse,
  cancellationType: string,
  message: string,
  user: { id: string; name?: string },
  percentage?: number,
  fee?: number,
) => {
  // This is a response to an existing note so there should always be notifications
  if (!order.notifications) return;

  // Create new cancellation terms.
  order.cancellationTerms = {
    definition: [
      {
        policy: createCancellationPolicy(cancellationType, percentage, fee),
        type: order.cancellationTerms?.definition?.[0].type,
        start: 0,
        end: 0,
      },
    ],
  };

  // Mark previous note as read
  order.notifications[order.notifications.length - 1] = {
    ...order.notifications[order.notifications.length - 1],
    read: true,
    type: 'CANCELLATION',
  };

  order.notifications.push({
    change: order.notifications[order.notifications.length - 1].change,
    date: dayjs().toISOString(),
    name: user.name,
    reason: order.notifications[order.notifications.length - 1].reason,
    details: `Cancellation Fee: ${displayCurrency(fee)} - Message: ${message}`,
    userId: user.id,
    type: 'CANCELLATION',
  });

  const orderUpdateRequest = {
    orderCancellationRequest: order.orderCancellationRequest,
    orderRefundRequest: undefined,
    notifications: order.notifications,
    state: order.state,
    cancellationTerms: order.cancellationTerms,
    groupPaymentPlan: order.groupPaymentPlan,
    orderRefundFee: order.orderRefundFee,
    orderRefundAmount: order.orderRefundAmount,
  };

  await updateOrder(order._id!, orderUpdateRequest);
};
